import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableHeadingCell,
  Button,
  PAYPAL_THEME,
  Row,
  Col,
  BodyText,
  CaptionText,
} from '@paypalcorp/pp-react'
import React, { useContext, useEffect, useState } from 'react'
import { useLazyQuery, useQuery, useMutation } from '@apollo/client'
import clientLogger from 'ppbl/utils/client-logger'
import { getCalculatorOffersByEvaluationId } from '../../graphql/calculator/queries'
import SpinnerAdapter from '../../components/SpinnerAdapter'
import { CleverContext } from './cleverContext'
import { financialCalculatorService } from '../../services/financialCalculator.service'
import {
  getCurrencyFormat,
  getLocalizedText,
} from 'ppbl/utils/locale-helper.js'
import { acceptOfferMutation } from '../../graphql/calculator/mutations'
import {
  convertTermToWeeks,
  roundOffNumberValue,
  getLoanFee,
  getNumberFromString,
  isApplicationAutomationEligible,
} from '../../utils/clever-utils'
import { LOAN_MAX_TERM } from 'ppbl/utils/cleverConstants'
import { isEmpty } from 'lodash-es'
import serverData from 'ppbl/utils/server-data'
import './styles.css'

const warningBorder = '#ff9600'
const warningBackground = '#fffbf5'
const needsAttentionBackground = '#fff7f7'
const bankVerifiedBorder = '#23b979'
const bankVerifiedBackground = '#bdffe2'
const automationBackground = '#23b979'

const { sysColorErrorMain, sysColorStructureBorderHighContrast } = PAYPAL_THEME

type Props = {
  opportunityId: string
}

enum offerStatus {
  CREATED = 'CREATED',
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

const CleverOfferCalculatorTable = ({ opportunityId }: Props) => {
  const { customOfferConstraints, application, reloadTable, setReloadTable } =
    useContext(CleverContext)

  const [createdOffers, setCreatedOffer] = useState([])
  const [isAcceptOfferFailed, setAcceptOfferFailed] = useState(false)
  const [isCalculatorResultsFailed, setCalculatorResultsFailed] =
    useState(false)
  const [accpetedOfferId, setAcceptedOfferId] = useState('')
  const partnerIdentityAccessToken = serverData.cleverAccessToken

  const calculateOfferPricingTier = (
    offer,
    offerAmount,
    termInWeeks,
    loanFee,
  ) => {
    if (!offer) {
      return null
    }

    const pricingTiers = [
      'No Approval Needed',
      'Tier 1 - Team Lead',
      'Tier 2 - Section Manager',
      'Tier 3 - Channel Manager',
      'Tier 4 - Head of Sales',
      'Needs Bank Approval',
    ]

    let offerpricingTierName = pricingTiers[1]
    let offerpricingTier = 1

    const noApprovalLoanFee = roundOffNumberValue(
      financialCalculatorService.getTotalFinanceCharges(
        offerAmount,
        termInWeeks,
        offer.weeklyInterestRate,
      ),
    )

    const roundedOffLoanFee = roundOffNumberValue(loanFee)

    if (roundedOffLoanFee >= noApprovalLoanFee) {
      return {
        offerpricingTierName: pricingTiers[0],
        offerpricingTier: 0,
      }
    }

    const offerTiers = offer.tiers
    let lastTierLoanFee = noApprovalLoanFee

    offerTiers.forEach((offerTier) => {
      const tierLoanFee = roundOffNumberValue(
        financialCalculatorService.getTotalFinanceCharges(
          offerAmount,
          termInWeeks,
          offerTier.weeklyInterestRate,
        ),
      )

      if (
        tierLoanFee !== 0 &&
        roundedOffLoanFee < lastTierLoanFee &&
        roundedOffLoanFee >= tierLoanFee
      ) {
        offerpricingTierName = pricingTiers[offerTier.tier]
        offerpricingTier = offerTier.tier
        lastTierLoanFee = tierLoanFee
      }
    })

    if (roundedOffLoanFee < lastTierLoanFee) {
      offerpricingTierName = pricingTiers[5]
      offerpricingTier = 5
    }

    return {
      offerpricingTierName,
      offerpricingTier,
    }
  }

  const getOfferRowStyle = (
    offerpricingTier,
    isQualified,
    status,
    automationQualified,
    termInWeeks,
    verifiedBankAccounts,
  ) => {
    let offerRowStyle = {
      borderBottomStyle: 'solid',
      borderWidth: 'thin',
    }

    let buttonStyle = {}
    let buttonText = 'Accept Offer'

    const rowColorStyles = {
      needsAttention: {
        borderBottomColor: sysColorErrorMain,
        backgroundColor: needsAttentionBackground,
      },
      warning: {
        backgroundColor: warningBackground,
        borderBottomColor: warningBorder,
      },
      bankVerified: {
        backgroundColor: bankVerifiedBackground,
        borderBottomColor: bankVerifiedBorder,
      },
      automationQualified: {
        borderBottomColor: automationBackground,
      },
    }

    const rowButtonStyles = {
      needsAttention: {
        borderColor: sysColorErrorMain,
        color: sysColorErrorMain,
      },
      warning: {
        borderColor: warningBorder,
        color: warningBorder,
      },
      bankVerified: {
        borderColor: bankVerifiedBorder,
        color: bankVerifiedBorder,
      },
      automationQualified: {
        borderColor: automationBackground,
        color: automationBackground,
      },
    }
    
    if (offerpricingTier > 0 && offerpricingTier < 5) {
      offerRowStyle = {
        ...offerRowStyle,
        ...rowColorStyles.warning,
      }
      buttonStyle = {
        ...buttonStyle,
        ...rowButtonStyles.warning,
        background: 'repeat-x',
      }
      buttonText = 'Get Approval'
    }
    if (!isQualified || offerpricingTier >= 5) {
      offerRowStyle = {
        ...offerRowStyle,
        ...rowColorStyles.needsAttention,
      }
      buttonStyle = {
        ...buttonStyle,
        ...rowButtonStyles.needsAttention,
        background: 'repeat-x',
      }
      buttonText = 'Get Approval'
    }
    if (status === offerStatus.APPROVED) {
      buttonText = 'Accept Offer'
      buttonStyle={}
    }
    if (automationQualified) {
      offerRowStyle = {
        ...offerRowStyle,
        ...rowColorStyles.automationQualified,
      }
      buttonStyle = {
        ...buttonStyle,
        ...rowButtonStyles.automationQualified,
        background: 'repeat-x',
      }
    }
    if (
      automationQualified &&
      termInWeeks <= LOAN_MAX_TERM &&
      verifiedBankAccounts
    ) {
      offerRowStyle = {
        ...offerRowStyle,
        ...rowColorStyles.bankVerified,
      }
      buttonStyle = {
        ...buttonStyle,
        ...rowButtonStyles.bankVerified,
        background: 'repeat-x',
      }
    }

    return {
      offerRowStyle,
      buttonStyle,
      buttonText,
    }
  }

  const calculateCalculatorOfferResults = (calculatorofferResultsData) => {
    let offersList = []

    calculatorofferResultsData.forEach((offerCalculaterResult, index) => {
      const {
        id,
        formattedAmount,
        formattedWeeklyInterestRate,
        formattedTerm,
        status,
        isQualified,
      } = offerCalculaterResult

      const { formattedTimeDuration } = formattedTerm

      const { amount } = formattedAmount
      const { percentage } = formattedWeeklyInterestRate

      const {
        business: { verifiedBankAccounts = false },
        tags = null,
      } = application

      const termInDays = getNumberFromString(formattedTimeDuration)
      const termInWeeks = convertTermToWeeks(termInDays)

      const offerAmountValue = Number(getNumberFromString(amount))
      const weeklyInterestRate = getNumberFromString(percentage)

      const loanFee = getLoanFee(
        weeklyInterestRate,
        termInWeeks,
        offerAmountValue,
      )
      const totalLoanAmount = offerAmountValue + loanFee

      const offer = financialCalculatorService.getOfferByTerm(
        customOfferConstraints,
        termInWeeks,
      )

      const {
        maxAutoQualifiedAmount: { value: maxAutomatedAmountValue },
      } = offer

      const { offerpricingTierName, offerpricingTier } =
        calculateOfferPricingTier(offer, offerAmountValue, termInWeeks, loanFee)

      const automationQualified =
        isApplicationAutomationEligible(tags) === 'Y' &&
        maxAutomatedAmountValue &&
        offerAmountValue <= maxAutomatedAmountValue

      const { offerRowStyle, buttonStyle, buttonText } = getOfferRowStyle(
        offerpricingTier,
        isQualified,
        status,
        automationQualified,
        termInWeeks,
        verifiedBankAccounts,
      )

      offersList.push({
        offerId: id,
        advanceAmount: getCurrencyFormat(Number(offerAmountValue)),
        isQualified: isQualified ? 'True' : 'False',
        termInWeeks,
        purchaseAmount: getCurrencyFormat(Math.round(totalLoanAmount)),
        loanFee: getCurrencyFormat(Math.round(loanFee)),
        pricingTier: offerpricingTierName,
        offerRowStyle,
        buttonStyle,
        buttonText,
      })
    })

    setCreatedOffer(offersList)
  }

  const { loading } = useQuery(getCalculatorOffersByEvaluationId, {
    variables: {
      offerEvaluationInput: {
        offerEvaluationId: opportunityId,
        partnerIdentityAccessToken,
      },
    },
    skip: !opportunityId,
    onCompleted(calculatorofferResultsData) {
      clientLogger.cleverInfo(
        `Calculator results fetched for opportunityId: ${opportunityId}`,
      )
      const offersData =
        calculatorofferResultsData?.credit?.businessCredit?.offerEvaluation
          ?.offers
      calculateCalculatorOfferResults(offersData)
      setCalculatorResultsFailed(false)
    },
    onError(err) {
      clientLogger.cleverError(
        'Error while calling get Calculator offer results by Evaluation Id, redirecting to error page',
        err?.message,
      )
      setCalculatorResultsFailed(true)
    },
  })

  const [getCalculatorOfferResults] = useLazyQuery(
    getCalculatorOffersByEvaluationId,
    {
      fetchPolicy: 'network-only',
      onCompleted(calculatorofferResultsData) {
        clientLogger.cleverInfo(
          `Calculator results fetched for opportunityId: ${opportunityId}`,
        )
        const offersData =
          calculatorofferResultsData?.credit?.businessCredit?.offerEvaluation
            ?.offers
        calculateCalculatorOfferResults(offersData)
        setReloadTable(false)
        setCalculatorResultsFailed(false)
      },
      onError(err) {
        clientLogger.cleverError(
          'Error while calling get Calculator offer results by Evaluation Id, redirecting to error page',
          err?.message,
        )
        setReloadTable(false)
        setCalculatorResultsFailed(true)
      },
    },
  )

  const [acceptOffer, { loading: acceptOfferLoading }] = useMutation(
    acceptOfferMutation,
    {
      onCompleted(acceptOfferData) {
        clientLogger.cleverInfo(
          `Offer accepted for opportunityId: ${opportunityId}, offerId: `,
          acceptOfferData?.credit?.businessCredit?.acceptOffer?.offerId,
        )
        setAcceptOfferFailed(false)
      },
      onError(acceptOfferError) {
        clientLogger.cleverError(
          'Error while calling GraphQl clever acceptOffer end point, redicting to error page',
          acceptOfferError?.message,
        )
        setAcceptOfferFailed(true)
      },
    },
  )

  // eslint-disable-next-line consistent-return
  const handleButtonClick = (buttonText, offerId) => {
    if (buttonText === 'Get Approval') {
      const salesforceUrl =
        serverData.data.appConfig.application?.cleverApprovalSalesforceUrl
      return window.open(
        `${salesforceUrl}apex/PriceModificationRequest?id=${offerId}`,
        '_blank',
      )
    } else if (buttonText === 'Accept Offer') {
      setAcceptedOfferId(offerId)
      acceptOffer({
        variables: {
          acceptOfferInput: {
            partnerApplicationId: opportunityId,
            offerId,
            partnerIdentityAccessToken,
          },
        },
      })
    }
  }

  useEffect(() => {
    if (reloadTable) {
      getCalculatorOfferResults({
        variables: {
          offerEvaluationInput: {
            offerEvaluationId: opportunityId,
            partnerIdentityAccessToken,
          },
        },
      })
    }
  }, [reloadTable])

  if (loading || reloadTable || acceptOfferLoading) {
    return <SpinnerAdapter className="calculatorTableSpinner" />
  }

  if (isCalculatorResultsFailed) {
    return (
      <div className="offerCalculatorTableError">
        <Row>
          <Col>
            <BodyText>
              {getLocalizedText(
                'cleverCalculator-page.cleverOfferTable.calculatorResultsFetchErrorText',
              )}
            </BodyText>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              size="lg"
              id="calculatorResultsReload"
              onClick={() => setReloadTable(true)}
            >
              {getLocalizedText(
                'cleverCalculator-page.cleverOfferTable.calculatorResultsRetryButtonText',
              )}
            </Button>
          </Col>
        </Row>
      </div>
    )
  }

  if (isEmpty(createdOffers)) {
    return null
  }

  return (
    <Table
      id="offerCalculatorTable"
      align="center"
      isFullWidth
      style={{
        border: sysColorStructureBorderHighContrast,
        borderWidth: '1px',
        borderStyle: 'solid',
      }}
    >
      <TableHead>
        <TableRow>
          <TableHeadingCell>
            {getLocalizedText(
              'cleverCalculator-page.cleverOfferTable.tableAmountHeadingText',
            )}
          </TableHeadingCell>
          <TableHeadingCell>
            {getLocalizedText(
              'cleverCalculator-page.cleverOfferTable.tableTermHeadingText',
            )}
          </TableHeadingCell>
          <TableHeadingCell>
            {getLocalizedText(
              'cleverCalculator-page.cleverOfferTable.tableFeeHeadingText',
            )}
          </TableHeadingCell>
          <TableHeadingCell>
            {getLocalizedText(
              'cleverCalculator-page.cleverOfferTable.tableRepaymentHeadingText',
            )}
          </TableHeadingCell>
          <TableHeadingCell>
            {getLocalizedText(
              'cleverCalculator-page.cleverOfferTable.tableTierHeadingText',
            )}
          </TableHeadingCell>
          <TableHeadingCell>
            {getLocalizedText(
              'cleverCalculator-page.cleverOfferTable.tableQualifiedHeadingText',
            )}
          </TableHeadingCell>
          <TableHeadingCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {createdOffers.map((selectedOffer, index) => {
          const {
            offerId,
            advanceAmount,
            isQualified,
            termInWeeks,
            purchaseAmount,
            loanFee,
            pricingTier,
            offerRowStyle,
            buttonStyle,
            buttonText,
          } = selectedOffer

          return (
            <TableRow key={`selectedOffer${index}`}>
              <TableCell style={offerRowStyle}>{advanceAmount}</TableCell>
              <TableCell style={offerRowStyle}>{termInWeeks}</TableCell>
              <TableCell style={offerRowStyle}>{loanFee}</TableCell>
              <TableCell style={offerRowStyle}>{purchaseAmount}</TableCell>
              <TableCell style={offerRowStyle}>{pricingTier}</TableCell>
              <TableCell style={offerRowStyle}>{isQualified}</TableCell>
              <TableCell style={offerRowStyle}>
                <Button
                  size="sm"
                  id={`rowButton${index}`}
                  data-testid={`rowButton${index}`}
                  style={buttonStyle}
                  onClick={() => handleButtonClick(buttonText, offerId)}
                >
                  {buttonText}
                </Button>
                {accpetedOfferId === offerId && isAcceptOfferFailed && (
                  <CaptionText style={{ color: sysColorErrorMain }}>
                    {getLocalizedText(
                      'cleverCalculator-page.cleverOfferTable.tableAcceptOfferErrorText',
                    )}
                  </CaptionText>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default CleverOfferCalculatorTable
